import * as qs from 'qs';

import type { OAuthProvider } from '../../../shared/types/routes/auth/v2';

/**
 * Append a `?next=` deep-link param to an SSO entry path when present. Both the V2 social entry
 * (`socialRegistry.ts` `startLogin`) and the V2 SAML entry (`samlRoutes.ts` `handleSamlEntry`) read
 * `req.query.next` and stash it for the post-login redirect, so threading it here survives the IdP round-trip.
 */
export const withNext = (url: string, next?: string): string =>
  next ? `${url}?next=${encodeURIComponent(next)}` : url;

/**
 * The V2 social-login entry point for a provider — `GET /api/auth/login/social/{provider}`, registered by the
 * provider modules under `modules/PassportV2/socialProviders/*Provider.ts`.
 *
 * NOT the V1 `/account/{provider}` entry: the V2 route marks the session `socialAuthV2` so its priority callback
 * shadow routes the return back into the V2 login flow (org picker / MFA), rather than the V1 handler. Every V2
 * social button/redirect must go through here so the flow never silently falls back to V1.
 *
 * `inviteToken` is threaded for social signups: `startLogin` reads `req.query.inviteToken` onto the session so the
 * social strategy auto-joins the inviting org once the OAuth round-trip completes.
 */
export const socialLoginEntryUrl = (provider: OAuthProvider, next?: string, inviteToken?: string): string => {
  // `skipNulls` drops null/undefined but not empty strings; coerce blanks to undefined so an empty `next` or
  // `inviteToken` is omitted rather than serialized as `next=` / `inviteToken=` (matching `withNext`).
  const query = qs.stringify({ next: next || undefined, inviteToken: inviteToken || undefined }, { skipNulls: true });
  const base = `/api/auth/login/social/${provider}`;
  return query ? `${base}?${query}` : base;
};
